import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import filePdfO from '@iconify/icons-fa/file-pdf-o'
import fileTextO from '@iconify/icons-fa/file-text-o'
import fileExcelO from '@iconify/icons-fa/file-excel-o'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  XMarkIcon,
  TrashIcon,
  EyeIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  TruckIcon,
  PrinterIcon,
  DocumentArrowDownIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline'
import {
  MainPage,
  PageTitle,
  SecondaryButton,
  ModalQuestion,
  Modal,
  FormText,
  FormSelect,
  PaginationFooter,
  DispatchCreationFlow,
  DispatchPrintModal,
  ShipmentLabelDjangoModal,
  ModalLoading,
  ConfirmModalStatus,
} from '../../components'
import Dropdown from '../../components/Atoms/Dropdown'
import { ImportProgressBar } from '../../components/ShipmentBulkUpload/ImportProgressBar'
import { useBulkUploadProgress } from '../../hooks/useBulkUploadProgress'
import { MissingGuidesModal } from '../../components/Dispatch/MissingGuidesModal'
import { getManifestsByShipment } from '../../services/DispatchServices'
import { ManifestByShipmentDto } from '../../interfaces/Django/ManifestByShipmentDto'
import { useAppSelector } from '../../store/hooks'
import {
  getImport,
  getImportFile,
  cancelImport,
  deleteImport,
  executeImport,
  deleteImportDetail,
  createBulkUploadManifest,
} from '../../services/ShipmentBulkUploadServices'
import { alertService } from '../../services'
import { formatDateTime } from '../../utils/format'
import {
  exportTablePDF,
  exportTableXLSX,
  exportTableTXT,
  exportFormatoShipmentXLSX_V2WithCoords,
} from '../../utils/exports'
import { shipmentGroupsToTemplateRows } from '../../utils/bulkTemplateRows'
import { BULK_CORRECTION_EXTRA_COLUMNS } from '../../utils/bulkCorrectionColumns'
import { payloadJsonToShipmentGroup } from '../../utils/shipmentPayload'
import { useBulkTemplateArgs } from '../../hooks/useBulkTemplateArgs'
import {
  headerStatusMeta,
  detailStatusMeta,
  resolveDetailStatusName,
  messageForCode,
  getBulkActionFlags,
  executedGuideNumbers,
  resolveHeaderStatusName,
  SERVER_ERROR_RETRY_CAP,
} from '../../utils/shipmentBulkUploadStatus'
import { DispatchGuideDto } from '../../interfaces'
import {
  getPendingManifestMark,
  savePendingManifestMark,
  clearPendingManifestMark,
  PendingManifestMark,
} from '../../utils/pendingManifestMark'
import {
  ImportDetailDTO,
  ImportHeaderDTO,
  ImportHeaderStatusName,
  ImportDetailStatusName,
  ImportProgressDTO,
  hasManifest,
} from '../../interfaces/Import/ShipmentBulkUploadApi'

const LIST_ROUTE = '/shipment/bulk-upload/list'

/**
 * Cuántas etiquetas se piden por vez.
 *
 * PROVISIONAL: el número real sale de medir `POST /label` con una carga grande.
 * `apiDjango` tiene `timeout: 30000` y 5000 etiquetas no se generan en 30 s por
 * ningún camino, así que pedirlas todas juntas garantiza un timeout mudo. Hasta
 * tener el dato, se imprime por tandas explícitas: el operador ve "Tanda 1 de 3"
 * y avanza. Mejor una tanda visible que un error a los 30 segundos.
 */
const LABEL_BATCH_LIMIT = 300

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

/** Etiqueta del dueño de la carga: nombre resuelto (sitio/cuenta) o, si falta, el ID. */
function ownerLabel(h: ImportHeaderDTO): string {
  if (h.billing_account_name) return h.billing_account_name
  if (h.account_name) return h.account_name
  if (h.billing_account != null && h.billing_account !== 0)
    return `Sitio ${h.billing_account}`
  if (h.account_id) return h.account_id
  return '–'
}

const ShipmentBulkUploadDetail: FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const headerId = id ?? null

  const user = useAppSelector((s) => s.user)

  const [header, setHeader] = useState<ImportHeaderDTO | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(true)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [actionBusy, setActionBusy] = useState(false)
  const [restartSignal, setRestartSignal] = useState(0)

  // Tabla de detalles: buscador (cualquier columna), paginado y page-size (client-side,
  // los detalles llegan todos en el GET). Mismo patrón que FavoriteDestination/consult.
  const [detailSearch, setDetailSearch] = useState('')
  const [detailPage, setDetailPage] = useState(0)
  const [detailPageSize, setDetailPageSize] = useState(10)

  // Modal de confirmación (cancelar / eliminar / eliminar detalle)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmTitle, setConfirmTitle] = useState('')
  const pendingAction = useRef<(() => void) | null>(null)

  // Modal ver payload (read-only)
  const [payloadOpen, setPayloadOpen] = useState(false)
  const [payloadText, setPayloadText] = useState('')

  // Info del header: al hacer click se despliega hacia abajo (hover = tooltip).
  const [infoOpen, setInfoOpen] = useState(false)

  // Generando la plantilla Excel con las guías en error (ExcelJS tarda con cargas grandes).
  const [generatingErrorFile, setGeneratingErrorFile] = useState(false)
  /**
   * Ya se bajó el archivo de errores en ESTA visita: se esconde "Descargar guías con
   * error" y en su lugar aparece "Subir archivo corregido".
   *
   * No se persiste a propósito: al recargar vuelve el botón de descargar, que es
   * justamente lo que necesita el operador que perdió o nunca guardó el Excel.
   */
  const [errorFileDownloaded, setErrorFileDownloaded] = useState(false)

  const prevDone = useRef(0)

  // --- Manifiesto de despacho ---
  const [manifestOpen, setManifestOpen] = useState(false)
  const [printOpen, setPrintOpen] = useState(false)
  const [printCode, setPrintCode] = useState('')
  /**
   * El manifiesto se creó pero la marca en Django falló. Banner persistente: el
   * operador necesita el Nº a mano para conciliar con soporte, y tiene que
   * seguir visible después de cerrar el modal.
   */
  const [markError, setMarkError] = useState<string | null>(null)
  /**
   * Marca pendiente: el manifiesto existe en .NET pero Django no quedó marcado.
   *
   * Se persiste en localStorage para sobrevivir al reload y al cierre de la
   * pestaña, y se reconcilia contra el servidor en cada carga del detalle.
   */
  const [pendingMark, setPendingMark] = useState<PendingManifestMark | null>(
    () => getPendingManifestMark(id),
  )
  const [markRetrying, setMarkRetrying] = useState(false)
  /**
   * Aviso de éxito del manifiesto.
   *
   * Vive en el HOST y no en `DispatchCreationFlow` porque el flujo se cierra
   * apenas el manifiesto se crea: si el aviso viviera adentro se desmontaría con
   * él y el operador se quedaría sin el número ni la opción de imprimir.
   */
  const [manifestSuccess, setManifestSuccess] = useState<{
    code: string
    message: string
  } | null>(null)
  /**
   * Destino del foco al abrir el modal.
   *
   * Apunta al encabezado del diálogo, no al buscador: `FormText` declara un prop
   * `ref` en su interfaz pero es un componente función plano, no un
   * `forwardRef`, así que React lo descarta y ese ref nunca llega al `<input>`.
   * Enfocar el encabezado es el patrón estándar de diálogo, mete el foco dentro
   * del modal (que es lo que hace funcionar Escape y la trampa de Tab) y además
   * los lectores de pantalla anuncian el título.
   */
  const manifestFocusRef = useRef<HTMLElement | null>(null)

  // --- Etiquetas ---
  const [labelOpen, setLabelOpen] = useState(false)
  const [labelBatch, setLabelBatch] = useState(0)

  /** Ya se atendió el `?manifest=1` con el que llega el atajo del listado. */
  const autoOpenedRef = useRef(false)

  /**
   * Ninguna guía de la carga quedó disponible para despacho.
   *
   * Se cierra el modal de creación y queda solo la explicación: crear un manifiesto
   * sin una sola guía de esta carga sería crear OTRO manifiesto, que además quedaría
   * marcado como el de esta carga y taparía el botón para el que sí corresponde.
   */
  const [noUsableGuides, setNoUsableGuides] = useState<{
    notInList: string[]
    withoutPieces: string[]
  } | null>(null)
  const [noUsableResults, setNoUsableResults] = useState<
    ManifestByShipmentDto[]
  >([])
  const [noUsableLoading, setNoUsableLoading] = useState(false)
  const [noUsableError, setNoUsableError] = useState<string | null>(null)

  const handleNoUsableGuides = useCallback(
    (info: { notInList: string[]; withoutPieces: string[] }) => {
    setManifestOpen(false)
    setNoUsableGuides(info)
    if (info.notInList.length === 0) return // nada que buscar: todas sin piezas
    setNoUsableLoading(true)
    setNoUsableError(null)
    getManifestsByShipment(info.notInList)
      .then((res) => {
        if (res.didError) {
          setNoUsableError(res.errorMessage || 'consulta no disponible')
          setNoUsableResults([])
          return
        }
        setNoUsableResults(res.model ?? [])
      })
      .catch((e: any) => {
        setNoUsableError(e?.message || 'consulta no disponible')
        setNoUsableResults([])
      })
      .finally(() => setNoUsableLoading(false))
    },
    [],
  )

  const fetchDetail = useCallback(async () => {
    if (!headerId) return
    const res = await getImport(headerId)
    if (res.didError) {
      setDetailError(messageForCode(res.code, res.errorMessage))
      setLoadingDetail(false)
      return
    }
    setHeader(res.model)
    setDetailError(null)
    setLoadingDetail(false)
  }, [headerId])

  // Carga inicial + recarga tras acciones (restartSignal).
  useEffect(() => {
    setLoadingDetail(true)
    fetchDetail()
  }, [fetchDetail, restartSignal])

  // Al completar: recargar el detalle final. El reintento de errores de servidor (78)
  // es manual (botón Reintentar) o del propio backend; no hay auto-retry del cliente.
  const onComplete = useCallback(
    async (_p: ImportProgressDTO) => {
      if (!headerId) return
      const res = await getImport(headerId)
      if (res.didError || !res.model) return
      setHeader(res.model)
    },
    [headerId],
  )

  const { progress, connectionLost, notFound } = useBulkUploadProgress(
    headerId,
    {
      onComplete,
      restartSignal,
    },
  )

  // Recargar detalle cuando avanza el conteo (filas nuevas procesadas).
  useEffect(() => {
    if (!progress) return
    const done = (progress.executed_count ?? 0) + (progress.error_count ?? 0)
    if (done !== prevDone.current) {
      prevDone.current = done
      if (!progress.is_complete) fetchDetail()
    }
  }, [progress, fetchDetail])

  // --- Estado derivado ---
  const statusName = progress?.status_name ?? header?.status_name ?? null
  const statusId = progress?.status_id ?? header?.status_id ?? null
  const meta = headerStatusMeta(statusName, statusId)
  /**
   * Estatus canónico, con el mismo fallback por `status_id` que el badge.
   *
   * Antes estos tres booleanos comparaban el string crudo mientras
   * `headerStatusMeta` sí caía al id: la pantalla decía "Finalizado" y el botón
   * de crear manifiesto no aparecía. Si el badge y un botón no coinciden, uno de
   * los dos miente.
   */
  const canonicalStatus = resolveHeaderStatusName(statusName, statusId)
  const isInProcess = canonicalStatus === ImportHeaderStatusName.InProcess
  const isFinalizedWithErrors =
    canonicalStatus === ImportHeaderStatusName.FinalizedWithErrors
  const isFinalized = canonicalStatus === ImportHeaderStatusName.Finalized

  // Detalle completo del header, sin truncar. Se usa tanto en el tooltip (hover)
  // como en el panel desplegable (click). Devuelve null si aún no hay header.
  const renderHeaderInfo = () => {
    if (!header) return null
    return (
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-sm">
        <dt className="text-gray-400">Estatus</dt>
        <dd className="text-gray-800">{meta.label}</dd>
        <dt className="text-gray-400">Fecha Inicio</dt>
        <dd className="text-gray-800">{formatDateTime(header.start_date)}</dd>
        {header.end_date && (
          <>
            <dt className="text-gray-400">Fecha Fin</dt>
            <dd className="text-gray-800">{formatDateTime(header.end_date)}</dd>
          </>
        )}
        <dt className="text-gray-400">Por</dt>
        <dd className="break-words text-gray-800">
          {header.creation_user_name ?? '–'}
        </dd>
        <dt className="text-gray-400">Línea de facturación</dt>
        <dd className="break-words text-gray-800">{ownerLabel(header)}</dd>
        {hasManifest(header) && (
          <>
            <dt className="text-gray-400">Manifiesto</dt>
            <dd className="break-words text-gray-800">
              {header.manifest_code ?? 'Creado'}
            </dd>
            <dt className="text-gray-400">Fecha del manifiesto</dt>
            <dd className="text-gray-800">
              {formatDateTime(header.manifest_create_date)}
            </dd>
          </>
        )}
      </dl>
    )
  }

  const details = useMemo(() => header?.details ?? [], [header])
  // Estado de los botones derivado de agregar los estatus de los detalles + la cabecera
  // (la spec exige calcularlo desde los detalles, no desde progress.*_count).
  const flags = useMemo(
    () => getBulkActionFlags(details, statusName, statusId),
    [details, statusName, statusId],
  )
  const executedCount = flags.executedCount
  const errorCount = flags.dataErrorCount + flags.serverErrorCount

  /**
   * Catálogos de la plantilla Excel (los mismos que usa la pantalla de creación).
   *
   * Solo se piden si esta carga TIENE errores: sin el `enabled` una carga finalizada
   * limpia dispararía cuatro peticiones para un botón que no se va a mostrar.
   */
  const bulkTemplate = useBulkTemplateArgs({ enabled: errorCount > 0 })

  /**
   * Universo de guías de la carga: las que EXISTEN, con número.
   *
   * No se usa `flags.executedCount` porque ese cuenta filas Executed incluidas
   * las que no tienen `shipment_number`, y una guía sin número no se puede
   * preseleccionar ni imprimir. Si se usara como universo, el aviso "vas a
   * incluir N de M" mentiría.
   */
  const bulkGuides = useMemo(() => executedGuideNumbers(details), [details])

  /**
   * Reconciliación: si el servidor YA dice que está manifestada, el pendiente
   * dejó de existir.
   *
   * Pasa cuando alguien la marcó desde otra pestaña, cuando el backend se
   * arregló, o cuando el reintento de otra sesión funcionó. Sin esto el banner
   * quedaría para siempre pidiendo reintentar algo que ya está hecho.
   */
  useEffect(() => {
    if (!header || !headerId) return
    if (!hasManifest(header)) return
    clearPendingManifestMark(headerId)
    setPendingMark(null)
    setMarkError(null)
  }, [header, headerId])

  /**
   * ¿La carga es del cliente activo?
   *
   * ESTO NO ES UN CONTROL DE SEGURIDAD. Es UX: evita que el operador abra un
   * modal que preseleccionaría cero guías. `GetAllMissingCCW` consulta por
   * `user.client`, y un mismo login opera varios clientes cambiándolos desde el
   * Header sin volver a loguear, así que con otro cliente activo la
   * preselección no matchearía nada y la feature parecería andar sin hacer nada.
   *
   * La autorización real tiene que estar en el backend: confirmado (2026-08-04)
   * que `POST /shipment-bulk/{id}/manifest` NO evalúa al usuario, igual que el
   * resto de `/shipment-bulk/*`, que reciben solo el GUID. Cualquiera puede
   * llamar la API directo salteándose esta guarda. Ver "A confirmar con backend"
   * en el plan.
   *
   * FALLA EN ABIERTO: si el header no trae ninguno de los dos campos, se
   * habilita. Un botón muerto para siempre es peor que uno que a veces
   * preselecciona cero.
   */
  const ownerMatches = useMemo(() => {
    if (!header) return false
    const sameAccount =
      !!header.account_id &&
      !!user.client?.id &&
      header.account_id.toLowerCase() === String(user.client.id).toLowerCase()
    const sameSite =
      header.billing_account != null &&
      user.client?.accountSiteID != null &&
      Number(header.billing_account) === Number(user.client.accountSiteID)
    if (header.account_id == null && header.billing_account == null) return true
    return sameAccount || sameSite
  }, [header, user.client?.id, user.client?.accountSiteID])

  // Buscador por cualquier columna (línea, estado, guía, guía aliada, mensaje, fecha).
  const filteredDetails = useMemo(() => {
    const tokens = detailSearch.toLowerCase().split(' ').filter(Boolean)
    if (!tokens.length) return details
    return details.filter((d) => {
      const haystack = [
        d.line_number,
        detailStatusMeta(d.status_name, d.status_id).label,
        d.shipment_number,
        d.shipment_header_reference,
        d.error_message,
        formatDateTime(d.executed_date),
      ]
        .map((v) => (v ?? '').toString().toLowerCase())
        .join(' ')
      return tokens.every((t) => haystack.includes(t))
    })
  }, [details, detailSearch])

  const paginatedDetails = useMemo(() => {
    const start = detailPage * detailPageSize
    return filteredDetails.slice(start, start + detailPageSize)
  }, [filteredDetails, detailPage, detailPageSize])

  // Export (PDF/Excel/TXT) del conjunto FILTRADO completo (no solo la página visible).
  const DETAIL_EXPORT_COLUMNS = [
    'Línea',
    'Estado',
    'Guía',
    'Guía aliada',
    'Mensaje',
    'Procesado',
  ]
  const buildDetailExportRows = (): (string | number)[][] =>
    filteredDetails.map((d) => [
      d.line_number,
      detailStatusMeta(d.status_name, d.status_id).label,
      d.shipment_number ?? '',
      d.shipment_header_reference ?? '',
      d.error_message ?? '',
      formatDateTime(d.executed_date),
    ])

  // --- Acciones ---
  const openConfirm = (title: string, action: () => void) => {
    setConfirmTitle(title)
    pendingAction.current = action
    setConfirmOpen(true)
  }
  const onAccepts: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
    if (value === true && pendingAction.current) pendingAction.current()
    pendingAction.current = null
  }

  const runAction = async (
    fn: () => Promise<{
      didError: boolean
      code: string
      errorMessage: string
    }>,
    okMsg: string,
    after?: () => void,
  ) => {
    setActionBusy(true)
    const r = await fn()
    setActionBusy(false)
    if (r.didError) {
      alertService.error(
        'No se pudo completar',
        messageForCode(r.code, r.errorMessage),
      )
      return
    }
    alertService.success(okMsg)
    if (after) after()
    else setRestartSignal((s) => s + 1)
  }

  const doCancel = () =>
    runAction(() => cancelImport(headerId!), 'Carga masiva cancelada')

  const doDelete = () =>
    runAction(
      () => deleteImport(headerId!),
      'Carga masiva eliminada',
      () => navigate(LIST_ROUTE),
    )

  const doRetry = () =>
    runAction(
      () => executeImport(headerId!, true),
      'Reintentando errores',
      () => setRestartSignal((s) => s + 1),
    )

  const doDeleteDetail = (detailId: string) =>
    runAction(
      () => deleteImportDetail(headerId!, detailId),
      'Detalle eliminado',
    )

  const doDownload = async () => {
    if (!headerId) return
    setActionBusy(true)
    const r = await getImportFile(headerId)
    setActionBusy(false)
    if (r.didError || !r.model?.url) {
      alertService.error(
        'No se pudo descargar',
        messageForCode(r.code, r.errorMessage),
      )
      return
    }
    window.open(r.model.url, '_blank', 'noopener')
  }

  /**
   * Descarga las guías con error en la PLANTILLA de carga masiva, ya llenas y con el
   * motivo del error en la última columna.
   *
   * Es el camino alterno a "Corregir errores": en vez de editar guía por guía en el
   * editor web, el operador arregla el archivo en Excel —donde ya sabe trabajar y donde
   * puede usar buscar/reemplazar sobre cientos de filas— y lo vuelve a subir como una
   * carga nueva.
   */
  const doDownloadErrorTemplate = async () => {
    if (!header || generatingErrorFile) return

    /**
     * El botón NO se deshabilita mientras faltan catálogos.
     *
     * `SecondaryButton` traduce `disabled` a `pointer-events-none`, así que un botón
     * deshabilitado no dispara NADA al hacer click: el operador ve que "no pasa nada" y no
     * tiene forma de saber por qué. Se prefiere dejarlo activo y decir en un aviso qué
     * falta —que además se resuelve solo en un par de segundos—.
     */
    if (!bulkTemplate.ready) {
      alertService.info(
        'Todavía no se puede generar el archivo',
        `${bulkTemplate.notReadyReason} La plantilla necesita las tiendas, ciudades y servicios de tu contrato para armar sus listas desplegables. Probá de nuevo en unos segundos.`,
      )
      return
    }

    if (!ownerMatches) {
      alertService.warn(
        'Esta carga masiva es de otro cliente',
        'El archivo se arma con las tiendas y servicios del cliente activo. Cambiá de cliente en el encabezado para bajarlo con los datos correctos.',
      )
      return
    }

    // Solo errores de DATO (75/77) — el mismo filtro que el editor `/fix`.
    //
    // Los de servidor (78) quedan afuera a propósito: se resuelven con "Reintentar", no
    // cambiando datos, y el `PUT /update` que recibe el archivo corregido solo acepta
    // 75/77. Incluirlos daría un archivo con filas que el operador corrige y que después
    // no se pueden volver a subir. Así el conjunto es el mismo de punta a punta.
    const errorDetails = details.filter((d) => {
      const s = resolveDetailStatusName(d.status_name, d.status_id)
      return (
        s === ImportDetailStatusName.Error ||
        s === ImportDetailStatusName.ErrorProcessing
      )
    })
    if (errorDetails.length === 0) {
      alertService.info('Esta carga masiva no tiene guías con error.')
      return
    }

    setGeneratingErrorFile(true)
    try {
      // Un payload_json corrupto de UNA fila no puede tumbar la descarga entera: se
      // descarta esa guía y se avisa cuántas quedaron afuera, que es información que el
      // operador necesita para no creer que el archivo está completo.
      const skipped: number[] = []
      const inputs = errorDetails.flatMap((d) => {
        try {
          const group = payloadJsonToShipmentGroup(d.payload_json, {
            identificationTypes: bulkTemplate.catalogs.identificationTypes,
            isCOD: bulkTemplate.isCODForReview,
          })
          return [
            {
              group,
              /**
               * Nº de guía aliada. Es la única columna cuya pérdida no deja rastro: el
               * parser descarta enteras las filas que no la traen.
               *
               * Se prefiere el del DETALLE, pero con FALLBACK al del payload
               * (`partner_shipment_id`): `shipment_header_reference` es nullable y viene
               * vacío en las filas que fallaron antes de llegar a crear la guía — o sea,
               * justo en las que este archivo existe para recuperar.
               */
              tracking:
                d.shipment_header_reference?.trim() ||
                group.shipmentHeaderReference?.trim() ||
                '',
              errorReason: d.error_message || '',
              // Columna oculta: es con esto que la fila reencuentra su línea al volver a
              // subir el archivo, aunque el operador le cambie el nº de guía aliada.
              lineId: d.shipment_bulk_upload_detail_id,
            },
          ]
        } catch {
          skipped.push(d.line_number)
          return []
        }
      })

      if (inputs.length === 0) {
        alertService.error(
          'No se pudo generar el archivo',
          'No se pudo reconstruir ninguna de las guías con error de esta carga. Corregilas desde "Corregir errores" o reportalo a soporte.',
        )
        return
      }

      // Guías que se quedaron SIN nº de guía aliada (ni en el detalle ni en el payload).
      // Se incluyen igual, con su fila en blanco en la columna A: excluirlas dejaría al
      // operador sin saber que existen, y así al menos puede escribir el número. Se avisa
      // porque, tal cual salen, el sistema las descarta al re-subir el archivo.
      const untracked = inputs.filter((i) => !i.tracking.trim())

      // Guías con error que COMPARTEN nº de guía aliada (causa frecuente del error): al
      // re-subir el archivo el sistema las fusiona en una sola, porque agrupa por ese
      // número. Se avisa antes de que el operador lo descubra con una guía de menos.
      const seen = new Set<string>()
      const duplicated = new Set<string>()
      inputs.forEach((i) => {
        const key = i.tracking.trim().toUpperCase()
        if (!key) return
        if (seen.has(key)) duplicated.add(i.tracking.trim())
        else seen.add(key)
      })

      const rows = shipmentGroupsToTemplateRows(inputs, {
        identificationTypes: bulkTemplate.catalogs.identificationTypes,
        citiesType: bulkTemplate.catalogs.citiesType,
        businessUnits: bulkTemplate.catalogs.businessUnitOptions,
        pieceCategories: bulkTemplate.catalogs.pieceCategories,
        originBusinessUnits: bulkTemplate.catalogs.originBusinessUnits,
        serviceOptions: bulkTemplate.catalogs.serviceOptions,
      })

      await exportFormatoShipmentXLSX_V2WithCoords(
        ...bulkTemplate.templateArgs,
        {
          rows,
          extraColumns: BULK_CORRECTION_EXTRA_COLUMNS,
          fileNamePrefix: `GuiasConError_${header.bulk_upload_number}`,
        },
      )

      // Habilita "Subir archivo corregido" en lugar del botón de descarga. Es estado de
      // esta visita nada más: al recargar vuelve a verse el de descargar, que es también
      // la salida para el que perdió el archivo.
      setErrorFileDownloaded(true)

      if (skipped.length > 0)
        alertService.warn(
          'Algunas guías quedaron fuera del archivo',
          `No se pudieron reconstruir ${skipped.length} guía(s) (línea${
            skipped.length > 1 ? 's' : ''
          } ${skipped.slice(0, 10).join(', ')}${skipped.length > 10 ? '…' : ''}). Corregilas desde "Corregir errores".`,
          { autoClose: false },
        )
      if (untracked.length > 0)
        alertService.warn(
          'Guías sin número de guía aliada',
          `${untracked.length} guía(s) quedaron con la columna "Número de guía aliada" vacía. Completala en el Excel antes de subir el archivo: sin ese número el sistema descarta la fila.`,
          { autoClose: false },
        )
      if (duplicated.size > 0)
        alertService.warn(
          'Números de guía aliada repetidos',
          `Estas guías comparten número: ${Array.from(duplicated).slice(0, 10).join(', ')}. Al volver a cargar el archivo se van a unir en una sola guía — cambiales el número antes de subirlo.`,
          { autoClose: false },
        )
      if (!flags.isHeaderClosed)
        alertService.info(
          'Evitá cargar dos veces las mismas guías',
          'Esta carga todavía admite "Reintentar" y "Corregir errores". Si además usás ese camino sobre las mismas guías, podrías terminar con guías duplicadas.',
        )
    } catch (e: any) {
      alertService.error(
        'No se pudo generar el archivo',
        e?.message || 'Ocurrió un error inesperado al armar el Excel.',
      )
    } finally {
      setGeneratingErrorFile(false)
    }
  }

  /**
   * Atajo del listado: `?manifest=1` abre el modal apenas hay datos suficientes.
   *
   * Se espera a tener header y guías porque el modal necesita la preselección; y
   * se limpia el parámetro de la URL para que un F5 o un "atrás" no lo reabran
   * solo. Una vez por visita, con guarda de ref.
   */
  useEffect(() => {
    if (autoOpenedRef.current) return
    if (searchParams.get('manifest') !== '1') return
    if (!header || !isFinalized || hasManifest(header)) return
    if (bulkGuides.length === 0 || !ownerMatches) return

    autoOpenedRef.current = true
    setManifestOpen(true)
    searchParams.delete('manifest')
    setSearchParams(searchParams, { replace: true })
  }, [
    searchParams,
    setSearchParams,
    header,
    isFinalized,
    bulkGuides.length,
    ownerMatches,
  ])

  /**
   * La carga terminó MIENTRAS el operador la miraba → se le ofrece crear el
   * manifiesto ahí mismo.
   *
   * Solo en la TRANSICIÓN. `prevCanonicalStatus` arranca en null y la primera
   * lectura solo lo siembra: entrar a una carga que ya estaba finalizada no
   * dispara nada. Sin esa guarda, cada visita a una carga vieja abriría un modal
   * que el operador no pidió.
   *
   * Se abre la confirmación, no el modal grande: es una interrupción, y el
   * operador tiene que poder decir que no.
   */
  /**
   * Se observó a esta carga en un estado que NO era Finalizado. Es lo que
   * distingue "terminó mientras la mirabas" de "entraste y ya estaba lista".
   */
  const sawNonFinalizedRef = useRef(false)
  /** Terminó mientras el operador miraba, y todavía no se le preguntó. */
  const pendingAskRef = useRef(false)
  const autoAskedOnFinishRef = useRef(false)

  // 1) DETECTAR.
  //
  // NO se compara "estatus anterior contra actual". La primera versión lo hacía y no
  // disparaba nunca: mientras la carga corre, el header reporta estatus que el mapa
  // de CABECERA no cubre ('En ejecucion' es un estatus de DETALLE, id 79, fuera del
  // rango 68–72), así que `canonicalStatus` es null y no había estatus previo que
  // comparar. Mismo choque nombre/id que ya rompió el botón de crear manifiesto.
  //
  // Lo que se recuerda es más simple y no depende de que el estatus intermedio esté
  // mapeado: ¿se vio alguna vez esta carga sin finalizar?
  useEffect(() => {
    // Sin ningún dato de estatus todavía no es "no finalizada", es "no sé".
    if (statusName == null && statusId == null) return

    if (canonicalStatus !== ImportHeaderStatusName.Finalized) {
      sawNonFinalizedRef.current = true
      return
    }
    // Finalizado. Solo se ofrece si antes se la vio sin finalizar: entrar a una
    // carga que ya estaba lista no abre ningún modal que el operador no pidió.
    if (sawNonFinalizedRef.current && !autoAskedOnFinishRef.current) {
      pendingAskRef.current = true
    }
  }, [canonicalStatus, statusName, statusId])

  // 2) CONSUMIR, cuando se pueda. Separado a propósito: al pasar a Finalizado el
  //    detalle se recarga en paralelo, así que en ese instante `bulkGuides` casi
  //    siempre está vacío todavía. Resuelto en un solo efecto, la transición se
  //    perdía justo en el caso normal.
  useEffect(() => {
    if (!pendingAskRef.current || autoAskedOnFinishRef.current) return
    // Mismas condiciones que el botón: sin guías o ya manifestada no hay nada que
    // ofrecer. Y con otro modal abierto se espera, en vez de pisar lo que el
    // operador está haciendo.
    if (!header || hasManifest(header)) return
    if (bulkGuides.length === 0 || !ownerMatches) return
    if (confirmOpen || manifestOpen || labelOpen) return

    autoAskedOnFinishRef.current = true
    pendingAskRef.current = false
    askCreateManifest()
    // `askCreateManifest` se redefine en cada render (cierra sobre `header`); meterlo
    // en deps re-dispararía el efecto sin aportar nada. La guarda de ref es la que manda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    header,
    bulkGuides.length,
    ownerMatches,
    confirmOpen,
    manifestOpen,
    labelOpen,
  ])

  const labelBatchCount = Math.max(
    1,
    Math.ceil(bulkGuides.length / LABEL_BATCH_LIMIT),
  )
  const labelSlice = useMemo(
    () =>
      bulkGuides
        .slice(
          labelBatch * LABEL_BATCH_LIMIT,
          (labelBatch + 1) * LABEL_BATCH_LIMIT,
        )
        .map((shipmentNumber) => ({ shipmentNumber })),
    [bulkGuides, labelBatch],
  )

  const askPrintLabels = () => {
    setLabelBatch(0)
    if (labelBatchCount > 1) {
      // El límite se dice ANTES de apretar, no después de un timeout.
      openConfirm(
        `Esta carga tiene ${bulkGuides.length} guías. Las etiquetas se imprimen de a ${LABEL_BATCH_LIMIT}, en ${labelBatchCount} tandas que vas a poder recorrer desde el visor. ¿Continuar?`,
        () => setLabelOpen(true),
      )
      return
    }
    setLabelOpen(true)
  }

  /**
   * Reintenta SOLO la marca en Django. El manifiesto ya existe: acá no se crea
   * nada, así que no hay riesgo de duplicarlo.
   */
  const retryMarkManifest = async () => {
    if (!headerId || !pendingMark) return

    setMarkRetrying(true)
    const res = await createBulkUploadManifest(headerId, pendingMark.id)
    setMarkRetrying(false)

    if (res.didError) {
      const msg = messageForCode(res.code, res.errorMessage)
      savePendingManifestMark(headerId, {
        code: pendingMark.code,
        id: pendingMark.id,
        lastError: msg,
      })
      setPendingMark(getPendingManifestMark(headerId))
      setMarkError(msg)
      return
    }

    clearPendingManifestMark(headerId)
    setPendingMark(null)
    setMarkError(null)
    setRestartSignal((s) => s + 1)
  }

  const askCreateManifest = () =>
    openConfirm(
      `¿Deseas crear un manifiesto basado en esta integración? Se preseleccionarán las ${bulkGuides.length} guías de la carga ${header?.bulk_upload_number ?? ''}.`,
      () => {
        setMarkError(null)
        setManifestOpen(true)
      },
    )

  /**
   * Fase 2 del guardado: el manifiesto YA existe en el API .NET.
   *
   * Un fallo acá no puede presentarse como fallo de creación. Se devuelve el
   * resultado al flujo (que lo anexa a su modal de éxito sin salir de PRINT) y
   * además se deja un banner persistente en la página, que sobrevive al cierre
   * del modal.
   */
  const handleManifestCreated = async (manifest: {
    code: string
    id?: string | null
    rejected: DispatchGuideDto[]
  }) => {
    // Las guías sin piezas no viajan: se dice ahora, no cuando el paquete no
    // llegue.
    const avisoPiezas = manifest.rejected.length
      ? `\n⚠ ${manifest.rejected.length} guía(s) sin piezas quedaron fuera: ${manifest.rejected
          .map((x) => x.numGuia)
          .join(', ')}`
      : ''

    if (!headerId) {
      setManifestSuccess({
        code: manifest.code,
        message: `¡Se registro el despacho Nº ${manifest.code} con exito!${avisoPiezas}`,
      })
      return { ok: true }
    }

    /**
     * Sin GUID no hay vínculo posible: el endpoint recibe `manifest_id` y es lo
     * único que manda. Tampoco tiene sentido dejar un pendiente reintentable —
     * el reintento fallaría igual. Se avisa y se manda a soporte con el número,
     * que es lo único accionable.
     */
    if (!manifest.id) {
      setMarkError(
        'El API de despacho no devolvió el identificador del manifiesto, así que no se pudo vincular con esta carga masiva.',
      )
      setManifestSuccess({
        code: manifest.code,
        message: `¡Se registro el despacho Nº ${manifest.code} con exito!${avisoPiezas}\n⚠ No se pudo vincular con la carga masiva. Reportalo a soporte con ese número.`,
      })
      return {
        ok: false,
        message: 'No se pudo vincular el manifiesto con la carga masiva.',
      }
    }

    const res = await createBulkUploadManifest(headerId, manifest.id)

    if (res.didError) {
      const msg = messageForCode(res.code, res.errorMessage)
      // Se persiste ANTES de mostrar nada: si el operador cierra la pestaña de
      // golpe, el pendiente sigue ahí cuando vuelva.
      savePendingManifestMark(headerId, {
        code: manifest.code,
        id: manifest.id ?? null,
        lastError: msg,
      })
      setPendingMark(getPendingManifestMark(headerId))
      setMarkError(msg)
      setManifestSuccess({
        code: manifest.code,
        message: `¡Se registro el despacho Nº ${manifest.code} con exito!${avisoPiezas}\n⚠ La carga masiva no quedó marcada como manifestada.`,
      })
      return {
        ok: false,
        message: 'La carga masiva no quedó marcada como manifestada.',
      }
    }

    setManifestSuccess({
      code: manifest.code,
      message: `¡Se registro el despacho Nº ${manifest.code} con exito!${avisoPiezas}`,
    })
    setRestartSignal((s) => s + 1)
    return { ok: true }
  }

  const openPayload = (d: ImportDetailDTO) => {
    setPayloadText(prettyJson(d.payload_json))
    setPayloadOpen(true)
  }

  // --- Render ---
  const importationNumber = header?.bulk_upload_number ?? 'Carga masiva'

  if (notFound) {
    return (
      <MainPage>
        <PageTitle
          title="Carga masiva"
          back
          onBack={() => navigate(LIST_ROUTE)}
        />
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          La carga masiva no existe o fue eliminada.
        </div>
      </MainPage>
    )
  }

  return (
    <MainPage>
      <PageTitle
        title={importationNumber}
        back
        onBack={() => navigate(LIST_ROUTE)}
      />

      {detailError && !header && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {detailError}
        </div>
      )}

      {loadingDetail && !header ? (
        <div className="animate-pulse space-y-4">
          <div className="h-24 rounded-lg bg-gray-100" />
          <div className="h-12 rounded-lg bg-gray-100" />
          <div className="h-64 rounded-lg bg-gray-100" />
        </div>
      ) : (
        header && (
          <div className="space-y-5">
            {/* Header card */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                {/* Grupo de info: hover = tooltip flotante, click = panel desplegable */}
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={infoOpen}
                  onClick={() => setInfoOpen((v) => !v)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setInfoOpen((v) => !v)
                    }
                  }}
                  className="relative flex min-w-0 cursor-pointer items-center gap-3 rounded-md py-0.5 pr-6 hover:bg-gray-50/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-main-400"
                >
                  <span
                    className={`shrink-0 whitespace-nowrap rounded-full px-3 py-0.5 text-xs font-semibold ${meta.badgeClass}`}
                  >
                    {meta.label}
                  </span>
                  <span className="max-w-[50rem] truncate whitespace-nowrap text-sm text-gray-500">
                    Fecha Inicio {formatDateTime(header.start_date)}
                    {header.end_date
                      ? ` · Fecha Fin ${formatDateTime(header.end_date)}`
                      : ''}
                  </span>
                  <span className="max-w-[12rem] truncate text-sm text-gray-500">
                    Por {header.creation_user_name}
                  </span>
                  <span className="max-w-[45rem] truncate text-sm text-gray-500">
                    Línea de facturación {ownerLabel(header)}
                  </span>
                  <ChevronDownIcon
                    className={`absolute right-1 h-4 w-4 shrink-0 text-gray-400 transition-transform ${
                      infoOpen ? 'rotate-180' : ''
                    }`}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* Reintentar / Corregir errores solo sobre una cabecera abierta.
                      En Finalizado, Cancelado o Eliminado los detalles siguen
                      teniendo sus errores, pero el backend ya no acepta cambios:
                      ahí la única acción que queda es descargar el archivo. */}
                  {errorCount > 0 && !flags.isHeaderClosed && (
                    <SecondaryButton
                      onClick={doRetry}
                      disabled={!flags.canRetry || actionBusy}
                      className="h-9 text-xs"
                    >
                      <ArrowPathIcon className="mr-1 h-4 w-4" /> Reintentar
                    </SecondaryButton>
                  )}
                  {errorCount > 0 && !flags.isHeaderClosed && (
                    <SecondaryButton
                      onClick={() =>
                        navigate(`/shipment/bulk-upload/${headerId}/fix`)
                      }
                      disabled={!flags.canEditErrors || actionBusy}
                      className="h-9 text-xs"
                    >
                      <PencilSquareIcon className="mr-1 h-4 w-4" /> Corregir
                      errores
                    </SecondaryButton>
                  )}
                  {/* Camino alterno a "Corregir errores": bajar las guías que
                      fallaron en la plantilla de carga masiva, arreglarlas en
                      Excel y volver a subir ESE MISMO archivo sobre esta carga.

                      Los dos botones se turnan: apenas se baja el archivo, el de
                      descargar deja lugar al de subir. Al recargar la página se
                      vuelve al de descargar (el estado no se persiste), que es la
                      salida para quien perdió el Excel.

                      A diferencia de Reintentar/Corregir, el de descargar SÍ se
                      muestra con la cabecera cerrada (Finalizado/Cancelado/
                      Eliminado): ahí el backend ya no acepta cambios sobre esta
                      carga, pero el archivo sigue sirviendo como carga nueva. */}
                  {flags.dataErrorCount > 0 && !errorFileDownloaded && (
                    <SecondaryButton
                      onClick={() => void doDownloadErrorTemplate()}
                      // Solo se deshabilita mientras el archivo se está generando.
                      // "Faltan catálogos" y "es de otro cliente" NO deshabilitan: el
                      // `disabled` de SecondaryButton aplica `pointer-events-none`, así
                      // que el click no dispararía nada y el operador vería que el botón
                      // no hace nada, sin ninguna explicación. El handler lo dice.
                      disabled={actionBusy || generatingErrorFile}
                      title={
                        !ownerMatches
                          ? 'Esta carga masiva pertenece a otro cliente. Cambiá de cliente en el encabezado para bajar el archivo con sus tiendas y servicios.'
                          : (bulkTemplate.notReadyReason ??
                            `Descarga las ${flags.dataErrorCount} guía(s) con error de datos en la plantilla de carga masiva, con el motivo de cada una. Corregilas en Excel y volvé a subir el archivo sobre esta misma carga.`)
                      }
                      className="h-9 text-xs"
                    >
                      {generatingErrorFile ? (
                        <ArrowPathIcon className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <DocumentArrowDownIcon className="mr-1 h-4 w-4" />
                      )}
                      Descargar guías con error
                    </SecondaryButton>
                  )}
                  {flags.dataErrorCount > 0 && errorFileDownloaded && (
                    <SecondaryButton
                      onClick={() => {
                        // La cabecera cerrada NO deshabilita el botón (quedaría mudo):
                        // se explica y se ofrece la alternativa real.
                        if (flags.isHeaderClosed) {
                          alertService.warn(
                            'Esta carga ya no admite correcciones',
                            'Está finalizada o cancelada, así que el sistema no puede actualizar sus guías. Podés subir el archivo corregido como una carga masiva nueva desde el menú de Carga masiva.',
                          )
                          return
                        }
                        navigate(
                          `/shipment/bulk-upload/${headerId}/fix/upload`,
                        )
                      }}
                      disabled={actionBusy}
                      title={
                        flags.isHeaderClosed
                          ? 'Esta carga ya está cerrada y no admite correcciones.'
                          : 'Subí el Excel que acabás de corregir. Se van a actualizar estas mismas guías, sin agregar ninguna nueva.'
                      }
                      className="h-9 text-xs"
                    >
                      <ArrowUpTrayIcon className="mr-1 h-4 w-4" /> Subir archivo
                      corregido
                    </SecondaryButton>
                  )}
                  {isInProcess && (
                    <SecondaryButton
                      onClick={() =>
                        openConfirm(
                          'Vas a cancelar la carga masiva. Las guías ya creadas quedan en el sistema; las pendientes no se procesarán. ¿Continuar?',
                          doCancel,
                        )
                      }
                      disabled={actionBusy}
                      className="h-9 text-xs"
                    >
                      <XMarkIcon className="mr-1 h-4 w-4" /> Cancelar
                    </SecondaryButton>
                  )}
                  {(isInProcess || isFinalizedWithErrors) && (
                    <SecondaryButton
                      onClick={() =>
                        openConfirm(
                          'Esta carga masiva se eliminará y no podrá recuperarse. ¿Continuar?',
                          doDelete,
                        )
                      }
                      disabled={!flags.canDeleteHeader || actionBusy}
                      title={
                        !flags.canDeleteHeader
                          ? 'No se puede eliminar: ya hay guías generadas en esta carga'
                          : undefined
                      }
                      className="h-9 text-xs"
                    >
                      <TrashIcon className="mr-1 h-4 w-4" /> Eliminar
                    </SecondaryButton>
                  )}
                  {header.source_file_path && (
                    <SecondaryButton
                      onClick={doDownload}
                      disabled={actionBusy}
                      className="h-9  text-xs"
                      title={
                        header.source_file_name
                          ? `Descargar ${header.source_file_name}`
                          : 'Descargar archivo'
                      }
                    >
                      <ArrowDownTrayIcon className="mr-1 h-4 w-4" /> Descargar
                      archivo
                    </SecondaryButton>
                  )}

                  {/* Etiquetas: también en Finalizado con errores. Imprimir las
                      guías que SÍ se crearon es inocuo, a diferencia del
                      manifiesto, que se gatilla solo en Finalizado. */}
                  {(isFinalized || isFinalizedWithErrors) &&
                    bulkGuides.length > 0 && (
                      <SecondaryButton
                        onClick={askPrintLabels}
                        disabled={actionBusy}
                        className="h-9 text-xs"
                        title={`Imprimir las ${bulkGuides.length} etiquetas de esta carga`}
                      >
                        <PrinterIcon className="mr-1 h-4 w-4" /> Imprimir
                        etiquetas
                      </SecondaryButton>
                    )}

                  {/* Crear manifiesto: solo Finalizado, sin manifiesto previo,
                      con guías reales y sobre una carga del cliente activo. */}
                  {isFinalized &&
                    !hasManifest(header) &&
                    bulkGuides.length > 0 && (
                      <SecondaryButton
                        onClick={askCreateManifest}
                        disabled={actionBusy || !ownerMatches}
                        title={
                          !ownerMatches
                            ? 'Esta carga masiva pertenece a otro cliente. Cambiá de cliente en el encabezado para poder despacharla.'
                            : undefined
                        }
                        className="h-9 text-xs"
                      >
                        <TruckIcon className="mr-1 h-4 w-4" /> Crear Manifiesto
                      </SecondaryButton>
                    )}

                  {/* El Nº y la fecha del manifiesto viven en el panel de info
                      (se despliega al hacer click), no acá: como chip al lado de
                      los botones competían por atención con las acciones. Queda
                      solo la acción. */}
                  {isFinalized &&
                    hasManifest(header) &&
                    header.manifest_code && (
                      <SecondaryButton
                        onClick={() => {
                          // El código va ANTES de abrir: el efecto de
                          // DispatchPrintModal tiene deps [typeUrl, open] y no
                          // reacciona a un cambio posterior de numberDispatch.
                          setPrintCode(header.manifest_code!)
                          setPrintOpen(true)
                        }}
                        className="h-9 text-xs"
                      >
                        <EyeIcon className="mr-1 h-4 w-4" /> Ver manifiesto
                      </SecondaryButton>
                    )}
                </div>
              </div>

              {/* Panel desplegable (click): crece hacia abajo sin alterar la fila,
                  con tope de altura y scroll para no romper la vista. */}
              {infoOpen && (
                <div className="mt-3 max-h-64 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3">
                  {renderHeaderInfo()}
                </div>
              )}
            </div>

            {/* El manifiesto se creó pero la marca falló. Persistente: el
                operador necesita el Nº para conciliar y tiene que seguir viéndolo
                después de cerrar el modal. */}
            {/* Marca pendiente. NO se puede descartar con una X: mientras la
                carga siga sin marcar, el botón "Crear Manifiesto" vuelve a
                aparecer y quien lo apriete genera un SEGUNDO manifiesto para las
                mismas guías. El banner desaparece solo cuando el servidor
                confirma que quedó marcada. */}
            {pendingMark && !hasManifest(header) && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <p className="font-semibold">
                  El manifiesto Nº {pendingMark.code} se creó correctamente,
                  pero esta carga masiva no quedó marcada como manifestada.
                </p>
                <p className="mt-1 text-red-700">
                  {markError ?? pendingMark.lastError}
                </p>
                <p className="mt-1 text-xs text-red-600">
                  Mientras no se marque, el botón "Crear Manifiesto" sigue
                  disponible y usarlo generaría un segundo manifiesto para las
                  mismas guías. Si el reintento no funciona, reportalo a soporte
                  con el número {pendingMark.code}.
                </p>
                <div className="mt-3">
                  <SecondaryButton
                    onClick={retryMarkManifest}
                    disabled={markRetrying}
                    className="h-9 text-xs"
                  >
                    <ArrowPathIcon
                      className={`mr-1 h-4 w-4 ${
                        markRetrying ? 'animate-spin' : ''
                      }`}
                    />
                    {markRetrying
                      ? 'Reintentando…'
                      : 'Reintentar actualizar manifiesto'}
                  </SecondaryButton>
                </div>
              </div>
            )}

            {/* Banner reconectando (CRITICAL GAP: nunca congela en silencio) */}
            {connectionLost && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800"
                aria-live="polite"
              >
                Reconectando… mostrando el último progreso conocido.
              </div>
            )}

            {/* Progreso */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <ImportProgressBar progress={progress} />
              {isFinalizedWithErrors && (
                <p className="mt-3 text-sm font-medium text-amber-700">
                  ⚠ {executedCount} de {header.total_details} guías creadas.{' '}
                  {errorCount} con error.
                </p>
              )}
            </div>

            {/* Toolbar: buscador + page-size + export */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="w-full sm:max-w-xs">
                <FormText
                  id="detail-search"
                  name="detail-search"
                  value={detailSearch}
                  placeholder="Buscar en cualquier columna…"
                  containerClassname="flex flex-1"
                  onChange={(e: any) => {
                    setDetailSearch(e.target.value)
                    setDetailPage(0)
                  }}
                />
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center text-xs text-gray-600">
                  <span>Mostrar</span>
                  <div className="mx-1 inline">
                    <FormSelect
                      id="detail-page-size"
                      name="detail-page-size"
                      label=""
                      selected={detailPageSize}
                      options={[10, 25, 50, 100]}
                      className="!h-8 !w-20 !px-2"
                      optionString={(o) => o.toString()}
                      onSelectOption={(o) => {
                        setDetailPageSize(o)
                        setDetailPage(0)
                      }}
                    />
                  </div>
                  <span>registros</span>
                </div>
                <div className="flex items-center gap-2">
                  <Dropdown
                    align="left"
                    label="Exportar"
                    items={[
                      {
                        items: [
                          {
                            label: 'EXCEL',
                            icon: (props) => (
                              <Icon
                                icon={fileExcelO}
                                className={props.className}
                              />
                            ),
                            onClick: () => {
                              if (!filteredDetails.length) return
                              exportTableXLSX(
                                DETAIL_EXPORT_COLUMNS,
                                buildDetailExportRows(),
                                'Detalle',
                                `CargaMasiva_${header.bulk_upload_number}`,
                              )
                            },
                          },
                          {
                            label: 'PDF',
                            icon: (props) => (
                              <Icon
                                icon={filePdfO}
                                className={props.className}
                              />
                            ),
                            onClick: () => {
                              if (!filteredDetails.length) return
                              exportTablePDF(
                                user.user!,
                                `CARGA MASIVA ${header.bulk_upload_number}`,
                                DETAIL_EXPORT_COLUMNS,
                                buildDetailExportRows(),
                                `CargaMasiva_${header.bulk_upload_number}`,
                              )
                            },
                          },
                          {
                            label: 'TXT',
                            icon: (props) => (
                              <Icon
                                icon={fileTextO}
                                className={props.className}
                              />
                            ),
                            onClick: () => {
                              if (!filteredDetails.length) return
                              exportTableTXT(
                                DETAIL_EXPORT_COLUMNS,
                                buildDetailExportRows(),
                                `CargaMasiva_${header.bulk_upload_number}`,
                              )
                            },
                          },
                        ],
                      },
                    ]}
                    buttonClassName="bg-main-500 hover:bg-main-400 hover:border-main-400 text-white focus-visible:outline-main-400 !pt-1 !pb-1 !text-xs !ring-0 h-[32px] items-center"
                    menuClassName="!left-0 !top-full !mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Tabla de detalles */}
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Nº</th>
                    <th className="px-3 py-2 font-medium">Estatus</th>
                    <th className="px-3 py-2 font-medium">Nº de guía</th>
                    <th className="px-3 py-2 font-medium">Nº de guía aliada</th>
                    <th className="px-3 py-2 font-medium">Mensaje</th>
                    <th className="px-3 py-2 font-medium">
                      Fecha de procesado
                    </th>
                    <th className="px-3 py-2 font-medium text-right">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedDetails.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-3 py-8 text-center text-sm text-gray-500"
                      >
                        {detailSearch
                          ? 'No hay detalles que coincidan con la búsqueda.'
                          : 'No hay detalles.'}
                      </td>
                    </tr>
                  )}
                  {paginatedDetails.map((d) => {
                    const dm = detailStatusMeta(d.status_name, d.status_id)
                    const canonical = resolveDetailStatusName(
                      d.status_name,
                      d.status_id,
                    )
                    const isExec = canonical === ImportDetailStatusName.Executed
                    const isErrServer =
                      canonical === ImportDetailStatusName.ErrorServer
                    // Filas con payload que vale la pena inspeccionar (errores + sustituida).
                    const isErrorish =
                      canonical === ImportDetailStatusName.Error ||
                      canonical === ImportDetailStatusName.ErrorProcessing ||
                      canonical === ImportDetailStatusName.ErrorServer ||
                      canonical === ImportDetailStatusName.Substituted
                    // Se puede eliminar salvo cuando ya generó el envío real (74).
                    const canDelete =
                      canonical !== ImportDetailStatusName.Executed
                    return (
                      <tr key={d.shipment_bulk_upload_detail_id}>
                        <td className="px-3 py-2 tabular-nums text-gray-700">
                          {d.line_number}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${dm.badgeClass}`}
                          >
                            {dm.label}
                          </span>
                          {isErrServer && (
                            <div className="mt-0.5 text-[11px] text-gray-400">
                              intento {d.retry_count ?? 0}/
                              {SERVER_ERROR_RETRY_CAP}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-700">
                          {d.shipment_number ?? '–'}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-700">
                          {d.shipment_header_reference ?? '–'}
                        </td>
                        <td
                          className="px-3 py-2 text-red-600 max-w-xs break-words whitespace-normal align-top"
                          title={d.error_message ?? undefined}
                        >
                          {d.error_message ?? '–'}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-gray-500">
                          {formatDateTime(d.executed_date)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex justify-end gap-2">
                            {isExec && (
                              <button
                                title="Ver guía"
                                onClick={() =>
                                  navigate(
                                    d.shipment_number
                                      ? `/shipments/consult?query=${encodeURIComponent(
                                          d.shipment_number,
                                        )}`
                                      : '/shipments/consult',
                                  )
                                }
                                className="text-main-500 hover:text-main-400"
                              >
                                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                              </button>
                            )}
                            {isErrorish && (
                              <button
                                title="Ver payload"
                                onClick={() => openPayload(d)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                title="Eliminar este detalle"
                                onClick={() =>
                                  openConfirm(
                                    `¿Eliminar la guía de la línea ${d.line_number}? No se procesará.`,
                                    () =>
                                      doDeleteDetail(
                                        d.shipment_bulk_upload_detail_id,
                                      ),
                                  )
                                }
                                disabled={actionBusy}
                                className="text-gray-500 hover:text-red-600"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <PaginationFooter
              currentPage={detailPage}
              itemsPerPage={detailPageSize}
              totalItems={filteredDetails.length}
              onPageChange={setDetailPage}
            />

            {/* Logs (últimos 200, solo WARNING/ERROR) */}
            {header.logs && header.logs.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h2 className="mb-2 text-sm font-semibold text-gray-700">
                  Logs
                </h2>
                <ul className="space-y-1 text-xs">
                  {header.logs.map((l) => (
                    <li
                      key={l.shipment_bulk_upload_log_id}
                      className="flex gap-2 text-gray-600"
                    >
                      <span
                        className={
                          l.event_type === 'ERROR'
                            ? 'font-semibold text-red-600'
                            : 'font-semibold text-amber-600'
                        }
                      >
                        {l.event_type}
                      </span>
                      <span className="tabular-nums text-gray-400">
                        {formatDateTime(l.log_date)}
                      </span>
                      <span>{l.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )
      )}

      <ModalQuestion
        open={confirmOpen}
        title={confirmTitle}
        setOpen={setConfirmOpen}
        setAccepts={onAccepts}
      />

      {/* Modal de despacho: el MISMO flujo de /Dispatch/create, con las guías de
          esta carga preseleccionadas.

          `Modal` y no `ModalWidthFull`: este usa overflow-visible y aquel
          overflow-hidden, y el combobox de Rutas de CreateDataDispatch es
          `absolute top-full` SIN portal, así que dentro de ModalWidthFull se
          recorta. `w-full` es obligatorio: el panel es flex item con width:auto
          y sin él se encoge al contenido. 80rem = 1280px = el max content width
          de DESIGN.md. */}
      <Modal
        open={manifestOpen}
        setOpen={setManifestOpen}
        className="w-full max-w-[95vw] flex flex-col"
        style={{ maxWidth: '80rem', minHeight: '80vh' }}
        staticBackdrop
        initialFocus={manifestFocusRef}
        onEscape={() => setManifestOpen(false)}
      >
        <div
          ref={manifestFocusRef as React.RefObject<HTMLDivElement>}
          tabIndex={-1}
          className="mb-4 flex items-start justify-between border-b border-gray-200 pb-3 outline-none"
        >
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Creación de manifiesto de despacho
            </h2>
            <p className="text-sm text-gray-500">
              Carga masiva {header?.bulk_upload_number}
            </p>
          </div>
          <button
            onClick={() => setManifestOpen(false)}
            aria-label="Cerrar"
            className="rounded p-1 hover:bg-gray-100"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {manifestOpen && (
          <DispatchCreationFlow
            embedded
            preselectedGuides={bulkGuides}
            onClose={() => setManifestOpen(false)}
            onNoUsableGuides={handleNoUsableGuides}
            onCreated={handleManifestCreated}
            onRequestPrint={(code) => {
              setPrintCode(code)
              setPrintOpen(true)
            }}
          />
        )}
      </Modal>

      {/* Vive acá y no dentro del flujo a propósito: el flujo se desmonta al
          cerrarse, y este aviso es justamente el que tiene que quedar. */}
      <MissingGuidesModal
        open={noUsableGuides !== null}
        onClose={() => setNoUsableGuides(null)}
        requested={noUsableGuides?.notInList ?? []}
        withoutPieces={noUsableGuides?.withoutPieces ?? []}
        allUnusable
        results={noUsableResults}
        loading={noUsableLoading}
        error={noUsableError}
      />

      {/* Aviso de éxito del manifiesto. Sobrevive al cierre del modal de
          creación, que es justo el punto: el flujo se cierra al guardar y el
          operador se queda con el número y la opción de imprimir. */}
      <ModalLoading
        open={!!manifestSuccess}
        title={manifestSuccess?.message ?? ''}
        status={ConfirmModalStatus.PRINT}
        setOpen={() => setManifestSuccess(null)}
        onPrint={() => {
          // El código va ANTES de abrir: el efecto de DispatchPrintModal tiene
          // deps [typeUrl, open] y no reacciona a un cambio posterior.
          setPrintCode(manifestSuccess?.code ?? '')
          setManifestSuccess(null)
          setPrintOpen(true)
        }}
      />

      <DispatchPrintModal
        open={printOpen}
        numberDispatch={printCode}
        setOpen={setPrintOpen}
      />

      {/* Montado bajo condición: `useShipmentLabel` dispara el POST según `open`,
          y el modal de despacho y este pelean por el iframe si conviven. */}
      {labelOpen && (
        <ShipmentLabelDjangoModal
          open={labelOpen}
          setOpen={setLabelOpen}
          shipment={labelSlice}
          format="pdf"
          batch={{
            index: labelBatch,
            total: labelBatchCount,
            onChange: setLabelBatch,
          }}
        />
      )}

      <Modal
        open={payloadOpen}
        setOpen={setPayloadOpen}
        style={{ maxWidth: '40rem' }}
      >
        <div className="flex items-center justify-between border-b border-gray-200 pb-2">
          <h2 className="text-base font-semibold text-gray-800">
            Guía (payload)
          </h2>
          <button onClick={() => setPayloadOpen(false)}>
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <pre className="mt-3 max-h-96 overflow-auto rounded bg-gray-50 p-3 text-xs text-gray-700">
          {payloadText}
        </pre>
      </Modal>
    </MainPage>
  )
}

export default ShipmentBulkUploadDetail
